import { useState } from 'react'
import { useAuth } from './AuthContext'
import { useTheme } from './theme'
import { api } from './api'
import ImmersivePinAuth from './components/auth/ImmersivePinAuth'
import PinForgotModal from './components/auth/PinForgotModal'
import ConnexionSuccess from './components/auth/ConnexionSuccess'
import ConnexionNav from './components/auth/ConnexionNav'
import { Web3Particles } from './components/auth/ConnexionParticles'

// Etapes PIN
const PIN_STEP = {
  LOGIN: 'login',       // Connexion téléphone + PIN
  REGISTER: 'register', // Inscription (nouveau compte)
}

// ============================================
// PAGE CONNEXION IMMERSIVE
// ============================================
export default function PageConnexion({ onNavigate, onSuccess }) {
  const { loginPin, registerPin } = useAuth()
  const { isWeb3 } = useTheme()

  // Etape PIN
  const [pinStep, setPinStep] = useState(PIN_STEP.LOGIN)

  // Etapes mode immersif (téléphone d'abord, puis PIN)
  const [immersifStep, setImmersifStep] = useState(1) // 1=phone, 2=pin/create

  // Champs formulaire
  const [nom, setNom] = useState('')
  const [prenom, setPrenom] = useState('')
  const [telephone, setTelephone] = useState('')
  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')

  // Etats
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  // Flow PIN oublie
  const [showPinForgot, setShowPinForgot] = useState(false)
  const [pinForgotPhone, setPinForgotPhone] = useState('')
  const [pinForgotLoading, setPinForgotLoading] = useState(false)
  const [pinForgotMessage, setPinForgotMessage] = useState('')
  const [pinForgotError, setPinForgotError] = useState('')

  // Styles selon theme — langage v4 (verre blanc creme + serif + pills orange)
  const t = {
    cardBg: isWeb3 ? 'bg-white/5' : 'bg-white/55',
    cardBorder: isWeb3 ? 'border-white/10' : 'border-white/80',
    textPrimary: 'text-ink',
    textSecondary: isWeb3 ? 'text-white/60' : 'text-ink/70',
    textMuted: isWeb3 ? 'text-white/60' : 'text-ink/50',
    inputBg: isWeb3 ? 'bg-white/5' : 'bg-white/70',
    inputBorder: isWeb3 ? 'border-white/20' : 'border-white/80',
    inputFocus: 'focus:border-primary',
    inputText: isWeb3 ? 'text-white placeholder-white/30' : 'text-ink placeholder-ink/45',
    tabBg: isWeb3 ? 'bg-white/5' : 'bg-ink/[0.05]',
    tabActive: isWeb3 ? 'bg-white/15 text-white' : 'bg-white text-ink shadow-sm',
    tabInactive: isWeb3 ? 'text-white/60 hover:text-white/70' : 'text-ink/60 hover:text-ink/80',
    errorBg: isWeb3 ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-600',
    linkColor: isWeb3 ? 'text-primary hover:text-primary/80' : 'text-primary hover:text-ink',
  }

  // Connexion téléphone + PIN
  const handleLoginPin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await loginPin(telephone, pin)
      setSuccess(true)
      setTimeout(() => {
        onSuccess ? onSuccess(false) : onNavigate('marche')
      }, 1000)
    } catch (err) {
      if (err.message?.includes('incorrect') || err.message?.includes('Unauthorized')) {
        setError('Téléphone ou code PIN incorrect. Si vous n\'avez pas de compte, cliquez sur "Créer un compte".')
      } else {
        setError(err.message || 'Erreur de connexion')
      }
    } finally {
      setLoading(false)
    }
  }

  // Inscription téléphone + PIN
  const handleRegisterPin = async (e) => {
    e.preventDefault()
    setError('')

    if (!telephone || telephone.replace(/\D/g, '').length < 10) {
      setError('Veuillez entrer un numéro de téléphone valide')
      return
    }

    if (pin !== pinConfirm) {
      setError('Les codes PIN ne correspondent pas')
      return
    }

    setLoading(true)

    try {
      await registerPin(telephone, pin, prenom, nom)
      setSuccess(true)
      setTimeout(() => {
        onSuccess ? onSuccess(true) : onNavigate('marche')
      }, 1000)
    } catch (err) {
      setError(err.message || 'Erreur lors de l\'inscription')
    } finally {
      setLoading(false)
    }
  }

  // Demande de reinitialisation PIN
  const handlePinForgot = async () => {
    setPinForgotError('')
    setPinForgotMessage('')

    if (!pinForgotPhone || pinForgotPhone.length < 10) {
      setPinForgotError('Veuillez entrer un numéro de téléphone valide')
      return
    }

    setPinForgotLoading(true)
    try {
      const result = await api.users.requestPinReset(pinForgotPhone)
      setPinForgotMessage(result.message || 'Votre demande a été envoyée.')
    } catch (err) {
      setPinForgotError(err.message || 'Erreur lors de l\'envoi de la demande')
    } finally {
      setPinForgotLoading(false)
    }
  }

  // Handler pour passer à l'étape suivante en mode immersif
  const handleImmersifNext = () => {
    if (!telephone || telephone.replace(/\D/g, '').length < 10) {
      setError('Veuillez entrer un numéro valide')
      return
    }
    setError('')
    setImmersifStep(2)
  }

  // Retour étape précédente mode immersif
  const handleImmersifBack = () => {
    setImmersifStep(1)
    setError('')
    setPin('')
    setPinConfirm('')
  }

  // « Créer un compte » : accessible directement (sans numéro prealable) — le
  // telephone est saisi dans le formulaire d'inscription a l'etape 2.
  const handleCreateAccount = () => {
    setError('')
    setPinStep(PIN_STEP.REGISTER)
    setImmersifStep(2)
  }

  // Sortie « ← Lyannaj » (Sujet 2 v7.1) : repasse en etat visiteur (reset telephone/PIN
  // + retour au hub). Sans elle, l'ecran de connexion piege l'utilisateur non authentifie.
  const handleExitToHub = () => {
    setError('')
    setTelephone('')
    setPin('')
    setPinConfirm('')
    setImmersifStep(1)
    setPinStep(PIN_STEP.LOGIN)
    onNavigate('marche', { immersive: false })
  }

  // Ecran de succes
  if (success) {
    return <ConnexionSuccess t={t} />
  }

  return (
    <div className={`min-h-screen relative ${isWeb3 ? 'portal-bg' : 'portal-bg-web2'}`}>
      {/* Bouton de sortie « ← Lyannaj » — verre, haut-gauche, position absolue (Sujet 2 v7.1) */}
      <button
        onClick={handleExitToHub}
        className="absolute top-[18px] left-[18px] z-20 rounded-full px-3.5 py-2 flex items-center gap-1.5 text-[12.5px] font-bold"
        style={{
          color: isWeb3 ? 'rgb(var(--c-creme))' : 'rgb(var(--c-green))',
          border: isWeb3 ? '1px solid rgba(247,240,234,0.18)' : '1px solid rgba(255,255,255,0.8)',
          background: isWeb3 ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.55)',
          boxShadow: '0 8px 24px rgba(7,62,61,0.10), inset 0 1px 0 rgba(255,255,255,0.6)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        ← <span className="font-serif">Lyannaj</span>
      </button>

      {/* Fond selon theme — v4 : crème + halos doux en derive */}
      {isWeb3 ? (
        <>
          <div className="portal-bg-gradient" />
          <div className="portal-grid" />
          <Web3Particles />
        </>
      ) : (
        <>
          <div className="portal-bg-gradient-web2" />
          <div className="portal-halos"><div className="portal-halo portal-halo-1" /><div className="portal-halo portal-halo-2" /></div>
        </>
      )}

      {/* Contenu */}
      <div className="min-h-screen flex items-center justify-center px-4 py-12 relative z-10">
        <div className="w-full max-w-md">
          <ConnexionNav />

          {/* Card — entonnoir unique : Telephone + PIN (verre creme v4) */}
          <div className={`${t.cardBg} backdrop-blur-xl rounded-[28px] p-6 border ${t.cardBorder} shadow-[0_8px_30px_rgba(7,62,61,0.08)]`}>
            <ImmersivePinAuth
              immersifStep={immersifStep}
              telephone={telephone}
              setTelephone={setTelephone}
              pin={pin}
              setPin={setPin}
              pinConfirm={pinConfirm}
              setPinConfirm={setPinConfirm}
              pinStep={pinStep}
              setPinStep={setPinStep}
              prenom={prenom}
              setPrenom={setPrenom}
              nom={nom}
              setNom={setNom}
              error={error}
              loading={loading}
              handleImmersifNext={handleImmersifNext}
              handleImmersifBack={handleImmersifBack}
              handleCreateAccount={handleCreateAccount}
              handleLoginPin={handleLoginPin}
              handleRegisterPin={handleRegisterPin}
              setShowPinForgot={setShowPinForgot}
              setPinForgotPhone={setPinForgotPhone}
              setPinForgotMessage={setPinForgotMessage}
              setPinForgotError={setPinForgotError}
              t={t}
            />
          </div>
        </div>
      </div>

      {/* Modal PIN oublie */}
      <PinForgotModal
        showPinForgot={showPinForgot}
        setShowPinForgot={setShowPinForgot}
        pinForgotPhone={pinForgotPhone}
        setPinForgotPhone={setPinForgotPhone}
        pinForgotLoading={pinForgotLoading}
        pinForgotMessage={pinForgotMessage}
        pinForgotError={pinForgotError}
        handlePinForgot={handlePinForgot}
      />
    </div>
  )
}
